import { useTranslation } from 'react-i18next';
import { logError } from '@/utils/errorHandler';

// Type definitions for translation objects
interface TranslationStep {
  title: string;
  text: string;
}

interface TranslationFeature {
  title: string;
  description: string;
  bullets: string[];
}

interface TranslationKeyFeature {
  title: string;
  alt: string;
  description: string;
}

interface TranslationResource {
  title: string;
  description: string;
}

interface TranslationFAQItem {
  question: string;
  answer: string;
}

interface TranslationComparisonRow {
  feature: string;
  basic: string | boolean;
  pro: string | boolean;
  business: string | boolean;
  ent: string | boolean;
}

interface TranslationPlatform {
  name: string;
  alt: string;
}

/**
 * Enterprise-grade hook for marketing page translations with type safety and error handling
 * Provides centralized access to all marketing page and modal translations
 */
export function useMarketingTranslations() {
  // Use the default translation namespace since marketingpage.json is merged into translation
  const { t } = useTranslation('translation');

  // Safe translation wrapper that always returns string
  const tSafe = (key: string, params?: Record<string, unknown>): string => {
    const result = t(key, params ?? {});
    return typeof result === 'string' ? result : String(result);
  };

  // Navigation translations
  const getNavigationText = (key: string): string => {
    return tSafe(`marketing.navigation.${key}`);
  };

  // Landing page translations
  const getLandingText = (
    section: string,
    key: string,
    params?: Record<string, unknown>
  ): string => {
    return tSafe(`marketing.landing.${section}.${key}`, params);
  };

  const getLandingHero = (key: string): string => {
    return tSafe(`marketing.landing.hero.${key}`);
  };

  const getLandingBenefits = (key: string): string => {
    return tSafe(`marketing.landing.benefits.${key}`);
  };

  const getLandingFeatures = (key: string): string => {
    return tSafe(`marketing.landing.keyFeatures.${key}`);
  };

  const getLandingResources = (key: string): string => {
    return tSafe(`marketing.landing.resources.${key}`);
  };

  const getLandingCTA = (key: string): string => {
    return tSafe(`marketing.landing.cta.${key}`);
  };

  const getLandingFooter = (key: string, params?: Record<string, unknown>): string => {
    return tSafe(`marketing.landing.footer.${key}`, params);
  };

  const getLandingInfographicSteps = (): TranslationStep[] => {
    try {
      const steps = t('marketing.landing.infographic.steps', {
        returnObjects: true,
      }) as TranslationStep[];

      return Array.isArray(steps) ? steps : [];
    } catch (error) {
      logError(
        'Error loading infographic steps',
        {
          component: 'useMarketingTranslations',
          action: 'getLandingInfographicSteps',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  };

  // Pricing page translations
  const getPricingText = (
    section: string,
    key: string,
    params?: Record<string, unknown>
  ): string => {
    return tSafe(`marketing.pricing.${section}.${key}`, params);
  };

  const getPricingHero = (key: string): string => {
    return tSafe(`marketing.pricing.hero.${key}`);
  };

  const getPricingTier = (tier: string, key: string, params?: Record<string, unknown>): string => {
    return tSafe(`marketing.pricing.tiers.${tier}.${key}`, params);
  };

  const getPricingFAQ = (key: string): string => {
    return tSafe(`marketing.pricing.faq.${key}`);
  };

  const getPricingCTA = (key: string): string => {
    return tSafe(`marketing.pricing.cta.${key}`);
  };

  const getPricingBilling = (key: string, params?: Record<string, unknown>): string => {
    return tSafe(`marketing.pricing.billingToggle.${key}`, params);
  };

  const getPricingComparison = (key: string): string => {
    return tSafe(`marketing.pricing.comparison.${key}`);
  };

  const getPricingAccessibility = (key: string): string => {
    return tSafe(`marketing.pricing.comparison.accessibility.${key}`);
  };

  // Plan names with proper word order for each language
  const getPlanName = (tier: string): string => {
    // Map 'pro' to 'professional' for translation consistency
    const tierKey = tier === 'pro' ? 'professional' : tier;
    return tSafe(`marketing.pricing.planNames.${tierKey}`);
  };

  // Product page translations
  const getProductText = (section: string, key: string): string => {
    return tSafe(`marketing.product.${section}.${key}`);
  };

  const getProductHero = (key: string): string => {
    return tSafe(`marketing.product.hero.${key}`);
  };

  const getProductFeature = (feature: string, key: string): string => {
    return tSafe(`marketing.product.features.${feature}.${key}`);
  };

  const getProductIntegrations = (key: string): string => {
    return tSafe(`marketing.product.integrations.${key}`);
  };

  const getProductTestimonial = (key: string): string => {
    return tSafe(`marketing.product.testimonial.${key}`);
  };

  const getProductCTA = (key: string): string => {
    return tSafe(`marketing.product.cta.${key}`);
  };

  // Auth/Login page translations
  const getAuthText = (section: string, key: string, params?: Record<string, unknown>): string => {
    return tSafe(`auth.${section}.${key}`, params);
  };

  const getLoginText = (key: string): string => {
    return tSafe(`auth.login.${key}`);
  };

  const getLoginBranding = (key: string): string => {
    return tSafe(`auth.login.branding.${key}`);
  };

  const getLoginForm = (key: string): string => {
    return tSafe(`auth.login.form.${key}`);
  };

  const getLoginField = (field: string, key: string): string => {
    return tSafe(`auth.login.form.fields.${field}.${key}`);
  };

  const getLoginButton = (button: string): string => {
    return tSafe(`auth.login.form.buttons.${button}`);
  };

  const getLoginError = (errorType?: string): string => {
    if (errorType) {
      return tSafe(`auth.login.form.errors.${errorType}`);
    }
    return tSafe('auth.login.form.errors.default');
  };

  const getLoginSignup = (key: string): string => {
    return tSafe(`auth.login.signup.${key}`);
  };

  const getLoginSecurity = (key: string): string => {
    return tSafe(`auth.login.security.${key}`);
  };

  const getLoginFooter = (key: string): string => {
    return tSafe(`auth.login.footer.${key}`);
  };

  // Modal translations
  const getModalText = (modal: string, key: string, params?: Record<string, unknown>): string => {
    return tSafe(`modal.${modal}.${key}`, params);
  };

  const getWalkthroughModal = (key: string, params?: Record<string, unknown>): string => {
    return tSafe(`modal.walkthrough.${key}`, params);
  };

  const getWalkthroughField = (field: string, key: string): string => {
    return tSafe(`modal.walkthrough.fields.${field}.${key}`);
  };

  const getWalkthroughSection = (section: string): string => {
    return tSafe(`modal.walkthrough.sections.${section}`);
  };

  const getWalkthroughButton = (button: string): string => {
    return tSafe(`modal.walkthrough.buttons.${button}`);
  };

  const getWalkthroughSuccess = (key: string): string => {
    return tSafe(`modal.walkthrough.success.${key}`);
  };

  // Contact modal translations
  const getContactModal = (key: string, params?: Record<string, unknown>): string => {
    return tSafe(`modal.contact.${key}`, params);
  };

  const getContactField = (field: string, key: string): string => {
    return tSafe(`modal.contact.fields.${field}.${key}`);
  };

  const getContactSection = (section: string): string => {
    return tSafe(`modal.contact.sections.${section}`);
  };

  const getContactButton = (button: string): string => {
    return tSafe(`modal.contact.buttons.${button}`);
  };

  const getContactSuccess = (key: string): string => {
    return tSafe(`modal.contact.success.${key}`);
  };

  // Invitation page translations
  const getInvitationText = (
    section: string,
    key: string,
    params?: Record<string, unknown>
  ): string => {
    return tSafe(`invitation.${section}.${key}`, params);
  };

  const getInvitationNavigation = (key: string): string => {
    return tSafe(`invitation.navigation.${key}`);
  };

  const getInvitationBranding = (key: string): string => {
    return tSafe(`invitation.branding.${key}`);
  };

  const getInvitationValidation = (key: string): string => {
    return tSafe(`invitation.validation.${key}`);
  };

  const getInvitationError = (key: string): string => {
    return tSafe(`invitation.errors.${key}`);
  };

  const getInvitationForm = (key: string, params?: Record<string, unknown>): string => {
    return tSafe(`invitation.form.${key}`, params);
  };

  const getInvitationField = (field: string, key: string): string => {
    return tSafe(`invitation.form.fields.${field}.${key}`);
  };

  const getInvitationButton = (button: string): string => {
    return tSafe(`invitation.form.buttons.${button}`);
  };

  const getInvitationSuccess = (key: string): string => {
    return tSafe(`invitation.success.${key}`);
  };

  const getInvitationTimeUnit = (key: string): string => {
    return tSafe(`invitation.timeUnits.${key}`);
  };

  // Demo page translations
  const getDemoHero = (key: string): string => {
    return tSafe(`marketing.demo.hero.${key}`);
  };

  const getDemoBenefits = (key: string): string => {
    return tSafe(`marketing.demo.benefits.${key}`);
  };

  const getDemoFeatures = (key: string): string => {
    return tSafe(`marketing.demo.features.${key}`);
  };

  const getDemoFAQ = (key: string): string => {
    return tSafe(`marketing.demo.faq.${key}`);
  };

  const getDemoFinalCTA = (key: string): string => {
    return tSafe(`marketing.demo.finalCta.${key}`);
  };

  const getDemoFAQItems = (): TranslationFAQItem[] => {
    try {
      const items = t('marketing.demo.faq.items', {
        returnObjects: true,
      });
      if (Array.isArray(items)) {
        return items.map((item: unknown) => {
          const i = item as Record<string, unknown>;
          return {
            question: String(i.question ?? ''),
            answer: String(i.answer ?? ''),
          };
        });
      }
    } catch (error) {
      logError(
        'Failed to load demo FAQ items',
        {
          component: 'useMarketingTranslations',
          action: 'getDemoFAQItems',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [];
  };

  // Complex object getters with proper error handling
  const getBenefitsFeatures = (): TranslationFeature[] => {
    try {
      const features = t('marketing.landing.benefits.features', {
        returnObjects: true,
      });
      if (Array.isArray(features)) {
        return features.map((feature: unknown) => {
          const f = feature as Record<string, unknown>;
          return {
            title: String(f.title ?? ''),
            description: String(f.description ?? ''),
            bullets: Array.isArray(f.bullets) ? f.bullets.map(String) : [],
          };
        });
      }
    } catch (error) {
      logError(
        'Failed to load benefits features',
        {
          component: 'useMarketingTranslations',
          action: 'getBenefitsFeatures',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [];
  };

  const getKeyFeaturesList = (): TranslationKeyFeature[] => {
    try {
      const features = t('marketing.landing.keyFeatures.features', {
        returnObjects: true,
      });
      if (Array.isArray(features)) {
        return features.map((feature: unknown) => {
          const f = feature as Record<string, unknown>;
          return {
            title: String(f.title ?? ''),
            alt: String(f.alt ?? ''),
            description: String(f.description ?? ''),
          };
        });
      }
    } catch (error) {
      logError(
        'Failed to load key features',
        {
          component: 'useMarketingTranslations',
          action: 'getKeyFeaturesList',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [];
  };

  const getResourcesList = (): TranslationResource[] => {
    try {
      const resources = t('marketing.landing.resources.items', {
        returnObjects: true,
      });
      if (Array.isArray(resources)) {
        return resources.map((resource: unknown) => {
          const r = resource as Record<string, unknown>;
          return {
            title: String(r.title ?? ''),
            description: String(r.description ?? ''),
          };
        });
      }
    } catch (error) {
      logError(
        'Failed to load resources',
        {
          component: 'useMarketingTranslations',
          action: 'getResourcesList',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [];
  };

  const getPricingFAQItems = (): TranslationFAQItem[] => {
    try {
      const items = t('marketing.pricing.faq.items', { returnObjects: true });
      if (Array.isArray(items)) {
        return items.map((item: unknown) => {
          const i = item as Record<string, unknown>;
          return {
            question: String(i.question ?? ''),
            answer: String(i.answer ?? ''),
          };
        });
      }
    } catch (error) {
      logError(
        'Failed to load FAQ items',
        {
          component: 'useMarketingTranslations',
          action: 'getPricingFAQItems',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [];
  };

  const getTierFeatures = (tier: string): string[] => {
    try {
      const features = t(`marketing.pricing.tiers.${tier}.features`, {
        returnObjects: true,
      });
      if (Array.isArray(features)) {
        return features.map((feature: unknown) => String(feature));
      }
    } catch (error) {
      logError(
        `Failed to load features for tier ${tier}`,
        {
          component: 'useMarketingTranslations',
          action: 'getTierFeatures',
          metadata: { error, tier },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [];
  };

  const getTierRealFeatures = (tier: string): string[] => {
    try {
      const features = t(`marketing.pricing.tiers.${tier}.realFeatures`, {
        returnObjects: true,
      });
      if (Array.isArray(features)) {
        return features.map((feature: unknown) => String(feature));
      }
    } catch (error) {
      logError(
        `Failed to load real features for tier ${tier}`,
        {
          component: 'useMarketingTranslations',
          action: 'getTierRealFeatures',
          metadata: { error, tier },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [];
  };

  const getComparisonTableRows = (): TranslationComparisonRow[] => {
    try {
      const rows = t('marketing.pricing.comparison.tableRows', {
        returnObjects: true,
      });
      if (Array.isArray(rows)) {
        return rows.map((row: unknown) => {
          const r = row as Record<string, unknown>;
          return {
            feature: String(r.feature ?? ''),
            basic: r.basic as string | boolean,
            pro: r.pro as string | boolean,
            business: r.business as string | boolean,
            ent: r.ent as string | boolean,
          };
        });
      }
    } catch (error) {
      logError(
        'Failed to load comparison table rows',
        {
          component: 'useMarketingTranslations',
          action: 'getComparisonTableRows',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [];
  };

  const getCompanySizeOptions = (): string[] => {
    try {
      const options = t('modal.contact.fields.companySize.options', {
        returnObjects: true,
      });
      if (Array.isArray(options)) {
        return options.map((option: unknown) => String(option));
      }
    } catch (error) {
      logError(
        'Failed to load company size options',
        {
          component: 'useMarketingTranslations',
          action: 'getCompanySizeOptions',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [
      '1-10 employees',
      '11-50 employees',
      '51-200 employees',
      '201-500 employees',
      '501-1000 employees',
      '1000+ employees',
    ];
  };

  const getPurposeOptions = (): string[] => {
    try {
      const options = t('modal.contact.fields.purpose.options', {
        returnObjects: true,
      });
      if (Array.isArray(options)) {
        return options.map((option: unknown) => String(option));
      }
    } catch (error) {
      logError(
        'Failed to load purpose options',
        {
          component: 'useMarketingTranslations',
          action: 'getPurposeOptions',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
    }
    return ['Request a Demo', 'Start a Free Trial', 'Speak with Sales', 'Get More Information'];
  };

  const getPreferredTimeOptions = (): string[] => {
    try {
      const options = t('modal.walkthrough.fields.preferredTime.options', {
        returnObjects: true,
      });
      if (Array.isArray(options)) {
        return options.map((option: unknown) => String(option));
      }
    } catch (error) {
      logError(
        'Failed to load preferred time options',
        {
          component: 'useMarketingTranslations',
          action: 'getPreferredTimeOptions',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [];
  };

  const getProductFeatureBullets = (feature: string): string[] => {
    try {
      const bullets = t(`marketing.product.features.${feature}.bullets`, {
        returnObjects: true,
      });
      if (Array.isArray(bullets)) {
        return bullets.map((bullet: unknown) => String(bullet));
      }
    } catch (error) {
      logError(
        `Failed to load bullets for feature ${feature}`,
        {
          component: 'useMarketingTranslations',
          action: 'getProductFeatureBullets',
          metadata: { error, feature },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [];
  };

  const getIntegrationPlatforms = (): TranslationPlatform[] => {
    try {
      const platforms = t('marketing.product.integrations.platforms', {
        returnObjects: true,
      });
      if (Array.isArray(platforms)) {
        return platforms.map((platform: unknown) => {
          const p = platform as Record<string, unknown>;
          return {
            name: String(p.name ?? ''),
            alt: String(p.alt ?? ''),
          };
        });
      }
    } catch (error) {
      logError(
        'Failed to load integration platforms',
        {
          component: 'useMarketingTranslations',
          action: 'getIntegrationPlatforms',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
    }
    return [];
  };

  // Helper for form field validation
  const getFieldError = (field: string, errorType?: string): string => {
    const baseKey = `modal.walkthrough.fields.${field}`;
    if (errorType) {
      return tSafe(`${baseKey}.${errorType}`);
    }
    return tSafe(`${baseKey}.error`);
  };

  return {
    // Direct translation function with type safety
    t: tSafe,

    // Navigation helpers
    getNavigationText,

    // Landing page helpers
    getLandingText,
    getLandingHero,
    getLandingBenefits,
    getLandingFeatures,
    getLandingResources,
    getLandingCTA,
    getLandingFooter,
    getLandingInfographicSteps,

    // Pricing page helpers
    getPricingText,
    getPricingHero,
    getPricingTier,
    getPricingFAQ,
    getPricingCTA,
    getPricingBilling,
    getPricingComparison,
    getPricingAccessibility,
    getPlanName,

    // Product page helpers
    getProductText,
    getProductHero,
    getProductFeature,
    getProductIntegrations,
    getProductTestimonial,
    getProductCTA,

    // Auth/Login page helpers
    getAuthText,
    getLoginText,
    getLoginBranding,
    getLoginForm,
    getLoginField,
    getLoginButton,
    getLoginError,
    getLoginSignup,
    getLoginSecurity,
    getLoginFooter,

    // Modal helpers
    getModalText,
    getWalkthroughModal,
    getWalkthroughField,
    getWalkthroughSection,
    getWalkthroughButton,
    getWalkthroughSuccess,

    // Contact modal helpers
    getContactModal,
    getContactField,
    getContactSection,
    getContactButton,
    getContactSuccess,

    // Invitation page helpers
    getInvitationText,
    getInvitationNavigation,
    getInvitationBranding,
    // getInvitationValidation, // not exported where unused to avoid unused vars in consumers
    getInvitationError,
    getInvitationForm,
    getInvitationField,
    getInvitationButton,
    getInvitationSuccess,
    getInvitationTimeUnit,

    // Demo page helpers
    getDemoHero,
    getDemoBenefits,
    getDemoFeatures,
    getDemoFAQ,
    getDemoFinalCTA,
    getDemoFAQItems,

    // Complex object getters
    getBenefitsFeatures,
    getKeyFeaturesList,
    getResourcesList,
    getPricingFAQItems,
    getTierFeatures,
    getTierRealFeatures,
    getComparisonTableRows,
    getCompanySizeOptions,
    getPurposeOptions,
    getPreferredTimeOptions,
    getProductFeatureBullets,
    getIntegrationPlatforms,

    // Utility helpers
    getFieldError,
  };
}

export type MarketingTranslations = ReturnType<typeof useMarketingTranslations>;
